import React, { useEffect, useRef, useState } from 'react';
import { Search, FileText, Users, IndianRupee, BarChart3, Rocket, CheckCircle, ChevronRight } from 'lucide-react';
import { servicesData } from '../../data/mock';

const iconMap = {
  Search,
  FileText,
  Users,
  IndianRupee,
  BarChart3,
  Rocket
};

const ServicesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="services"
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'var(--bg-page)' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <Rocket size={16} />
            <span className="font-semibold text-sm">What We Do</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {servicesData.title}
          </h2>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {servicesData.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className={`card p-6 md:p-8 h-full flex flex-col ${
                  isVisible ? 'animate-fadeInUp' : 'opacity-0'
                }`}
                style={{ animationDelay: `${(index + 3) * 100}ms`, background: 'white' }}
              >
                {/* Icon */}
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                  style={{ background: 'var(--brand-primary)' }}
                >
                  <Icon size={28} style={{ color: 'var(--brand-dark)' }} />
                </div>

                {/* Title */}
                <h3 className="heading-3 mb-3" style={{ color: 'var(--text-primary)' }}>
                  {service.title}
                </h3>

                {/* Description */}
                <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {service.description}
                </p>

                {/* Features */}
                <div className="flex-grow">
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <CheckCircle 
                          size={18} 
                          className="flex-shrink-0 mt-0.5"
                          style={{ color: 'var(--brand-dark)' }} 
                        />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Note */}
                <p 
                  className="body-small font-medium pt-4 border-t"
                  style={{ borderColor: 'rgba(0, 69, 52, 0.1)', color: 'var(--brand-dark)' }}
                >
                  {service.note}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
